import React, { useEffect } from "react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import useScrollReveal from "@/hooks/useScrollReveal";

/**
 * Generic legal/policy page wrapper.
 * props.sections: [{ heading, intro?, paragraphs?: string[], bullets?: string[], blocks?: {title, items: string[]}[] }]
 */
export default function LegalPage({
  title,
  crumb,
  eyebrow,
  tagline,
  hero,
  effectiveDate,
  sections,
  testId,
}) {
  useScrollReveal();
  useEffect(() => {
    document.title = `${title} · Pakkaland`;
  }, [title]);

  return (
    <SubpageLayout testId={testId}>
      <SubpageHero
        crumb={crumb}
        eyebrow={eyebrow}
        title={title}
        tagline={tagline}
        image={hero}
      />

      <section className="relative bg-white py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {effectiveDate && (
            <div className="mb-12 reveal">
              <div className="text-[0.65rem] tracking-[0.3em] uppercase text-pkred font-semibold">
                Hatályos
              </div>
              <div className="font-serif italic text-pkcharcoal text-lg mt-1">
                {effectiveDate}
              </div>
            </div>
          )}

          <div className="space-y-12">
            {sections.map((s, i) => (
              <article
                key={s.heading}
                data-testid={`legal-section-${i}`}
                className="reveal"
              >
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="text-pkred font-serif text-2xl font-extralight leading-none shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif text-pkcharcoal text-2xl sm:text-3xl leading-tight">
                    {s.heading}
                  </h2>
                </div>

                {s.intro && (
                  <p className="text-pkcharcoal/75 text-base leading-relaxed mb-4">
                    {s.intro}
                  </p>
                )}

                {s.paragraphs?.map((p, j) => (
                  <p
                    key={j}
                    className="text-pkcharcoal/75 text-base leading-relaxed mb-4"
                  >
                    {p}
                  </p>
                ))}

                {s.bullets && (
                  <ul className="list-disc pl-5 space-y-1.5 text-pkcharcoal/75 text-base leading-relaxed marker:text-pkred">
                    {s.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                )}

                {s.blocks?.map((b, j) => (
                  <div key={j} className="mt-5">
                    {b.title && (
                      <h3 className="font-serif text-lg text-pkcharcoal mt-4 mb-2">
                        {b.title}
                      </h3>
                    )}
                    {b.intro && (
                      <p className="text-pkcharcoal/75 text-base leading-relaxed mb-3">
                        {b.intro}
                      </p>
                    )}
                    {b.items && (
                      <ul className="list-disc pl-5 space-y-1.5 text-pkcharcoal/75 text-base leading-relaxed marker:text-pkred">
                        {b.items.map((it, k) => (
                          <li key={k}>{it}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </article>
            ))}
          </div>
        </div>
      </section>
    </SubpageLayout>
  );
}
